import React, { useEffect, useState } from 'react';
import {
    DownOutlined,
    InboxOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Button,
    Dropdown,
    message,
    Modal,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import Dragger from 'antd/es/upload/Dragger';       // CRA ≤v5
// import { Upload } from 'antd'; const { Dragger } = Upload; // CRA v5+
import axios from 'axios';

import DragHandle from './DragHandle';
import BookEditFormModal from '../modal/BookEditFormModal';
import { baseURL } from '../service/ApiService';

// ────────────────────────────────────────────────────────
// Row wrapper
// ────────────────────────────────────────────────────────
const Row = ({ children, ...props }) => {
    const {
        attributes,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props['data-row-key'] });

    const style = {
        ...props.style,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes}>
            {children}
        </tr>
    );
};

// ────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────
export default function Books() {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);

    const { Text } = Typography;

    // fetch table
    const updateTable = async () => {
        try {
            const { data } = await axios.get(`${baseURL}/api/book/all`);
            setDataSource(
                data
                    .sort((a, b) => a.order - b.order)     // keep visual order stable
                    .map((item) => ({ ...item, key: item.id }))
            );
            setLoading(false);
        } catch (err) {
            message.error('Failed to load books');
        }
    };

    useEffect(() => { updateTable(); }, []);

    const onDragEnd = async ({ active, over }) => {
        if (!over || active.id === over.id) return;

        // 1️⃣  Compute the NEW order first
        const oldRows = [...dataSource];
        const from     = oldRows.findIndex(r => r.key === active.id);
        const to       = oldRows.findIndex(r => r.key === over.id);
        const newRows  = arrayMove(oldRows, from, to);

        // 2️⃣  Optimistically update the UI
        setDataSource(newRows);

        // 3️⃣  Build a map:  { bookId: order }
        const orderMap = newRows.reduce((map, row, idx) => {
            map[row.id] = idx;          // id → new index
            return map;
        }, {});

        try {
            await axios.post(`${baseURL}/api/book/swap`, orderMap); // { "42":0, "17":1, … }
            message.success('Order saved');
        } catch (err) {
            message.error('Failed to save order, reverting');
            updateTable();              // rollback if server rejects
        }
    };

    // column definitions
    const columns = [
        {
            key: 'sort',
            width: 60,
            align: 'center',
            render: (_, record) => <DragHandle id={record.key} />,
        },
        {
            title: 'Order',
            dataIndex: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        { title: 'English Name', dataIndex: 'englishTitle', width: 200 },
        {
            title: 'Book Cover',
            dataIndex: 'coverImageUrl',
            render: (url) =>
                url ? <img src={url} alt="" style={{ width: 50 }} /> : '-',
        },
        {
            title: 'Book Tags',
            render: (row) =>
                row.tags.map((tag) => (
                    <Tag key={tag} color="blue">{tag}</Tag>
                )),
        },
        {
            title: 'Words',
            dataIndex: 'wordNum',
            width: 100,
            sorter: (a, b) => a.wordNum - b.wordNum,
        },
        {
            title: 'Users',
            dataIndex: 'userNum',
            width: 100,
            sorter: (a, b) => a.userNum - b.userNum,
        },
        {
            title: 'Action',
            key: 'operation',
            fixed: 'right',
            width: 100,
            render: (row) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: 'editBook',
                                label: (
                                    <BookEditFormModal
                                        buttonName="Edit"
                                        book={row}
                                        updateBookList={updateTable}
                                    />
                                ),
                            },
                        ],
                    }}
                >
                    <a onClick={(e) => e.preventDefault()}>
                        <Space>
                            Action
                            <DownOutlined />
                        </Space>
                    </a>
                </Dropdown>
            ),
        },
    ];

    // file-upload helpers
    const beforeUpload = (file) => {
        const maxSize = 20 * 1024 * 1024;      // 20 MB
        const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
        if (!isCSV) { message.error('CSV only'); return false; }
        if (file.size > maxSize) { message.error('Max 20 MB'); return false; }
        setFileList((prev) => [...prev, file]);
        return false;
    };

    const handleRemove = (file) =>
        setFileList((prev) => prev.filter((i) => i.uid !== file.uid));

    const handleUploadFiles = async () => {
        if (!fileList.length) return message.info('No file selected');
        setUploading(true);
        try {
            const fd = new FormData();
            fileList.forEach((f) => fd.append('files', f));
            await axios.post(`${baseURL}/api/book/upload`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            message.success('Upload success');
            setFileList([]);
            updateTable();
        } finally {
            setUploading(false);
        }
    };

    // render
    return (
        <>
            <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setOpen(true)}
                style={{ marginBottom: 16 }}
            >
                Upload new book
            </Button>

            <DndContext
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={dataSource.map((r) => r.key)}
                    strategy={verticalListSortingStrategy}
                >
                    <Table
                        components={{ body: { row: Row } }}
                        rowKey="key"
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        pagination={{ pageSize: 1000 }}  /* all rows on one page */
                        scroll={{ y: 600 }}
                    />
                </SortableContext>
            </DndContext>

            {/* Upload modal */}
            <Modal
                title="Upload a new book"
                centered
                open={open}
                onCancel={() => setOpen(false)}
                onOk={handleUploadFiles}
                okButtonProps={{ loading: uploading }}
                okText="Save"
                width={800}
            >
                <Text italic>
                    Please name the CSV file after the book and put each word on its own
                    line.
                </Text>
                <Dragger
                    name="file"
                    multiple
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onRemove={handleRemove}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">
                        Click or drag files here (CSV, max&nbsp;20&nbsp;MB)
                    </p>
                </Dragger>
            </Modal>
        </>
    );
}
